import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, inject, Sidebar as SidebarFrame, SidebarLink } from 'Mri.Web.Common';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import displayIcon from '../../../Mri.Web.Common/Assets/Icons/display-icon.svg';
import analyticsIcon from '../../../Mri.Web.Common/Assets/Icons/analytics-icon.png';
import boldvuIcon from '../../../Mri.Web.Common/Assets/boldvu.png';
import collapsedIcon from '../../../Mri.Web.Common/Assets/MRI-favicon-light.png';
import mountainIcon from '../../../Mri.Web.Common/Assets/polygons-1.png';
import { AccountStore } from '../Account/AccountStore';
import { UserRoleViews } from '../Users/UserRoleViewSelector';
import { AppStore } from './AppStore';
import { BsBuilding } from '@react-icons/all-files/bs/BsBuilding';
import { BsClockHistory } from '@react-icons/all-files/bs/BsClockHistory';
import { BsExclamationTriangleFill } from '@react-icons/all-files/bs/BsExclamationTriangleFill';
import { BsEye } from '@react-icons/all-files/bs/BsEye';
import { BsPeopleFill } from '@react-icons/all-files/bs/BsPeopleFill';
import { BsQuestionCircle } from '@react-icons/all-files/bs/BsQuestionCircle';
import { BsBriefcase } from '@react-icons/all-files/bs/BsBriefcase';

export interface ISidebarProps extends RouteComponentProps<undefined> {
    readonly closeSidebar: any;
    readonly collapseSidebar: any;
    readonly sidebarVisible: boolean;
    readonly sidebarCollapsed: boolean;
}

@autoinject
@observer
export class Sidebar extends React.Component<ISidebarProps> {
    public render() {
        return (
            <SidebarFrame
                title={this.sidebarTitle}
                visible={this.props.sidebarVisible}
                className={this.collapsedClass}
                links={this.renderLinks}
                footer={this.renderFooter}
                collapseSidebar={this.props.collapseSidebar}
                sidebarCollapsed={this.props.sidebarCollapsed}
                isMedium={this.appStore.isMedium}
            />
        );
    }

    @computed
    get renderLinks() {
        return (
            <ul>
                {this.accountStore.isInRoles(['MriAdmin', 'MriUser', 'PortalUser', 'PortalAdmin']) && (
                    <SidebarLink
                        customIcon={<BsBriefcase className="nav-icon img-fluid inc-width" />}
                        label="Dashboard"
                        exact
                        strict
                        url="/"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    />
                )}

                {this.accountStore.isInRoles(['MriAdmin', 'MriUser', 'PortalUser', 'PortalAdmin']) && (
                    <SidebarLink
                        customIcon={<img className="nav-icon img-fluid inc-width" src={displayIcon} />}
                        label="Displays"
                        url="/displays"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    />
                )}

                {this.accountStore.isInRoles(['MriAdmin', 'MriUser', 'PortalUser', 'PortalAdmin']) && (
                    <SidebarLink
                        customIcon={<BsClockHistory className="nav-icon img-fluid inc-width" />}
                        label="Display Alerts"
                        url="/alert-rules"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    />
                )}

                {this.accountStore.isInRoles(['MriAdmin', 'MriUser', 'PortalUser', 'PortalAdmin']) && (
                    <SidebarLink
                        customIcon={<BsEye className="nav-icon img-fluid inc-width" />}
                        label="Watchlist"
                        url="/favorites"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    />
                )}

                {this.accountStore.isInRoles([
                    'MriAdmin',
                    'MriUser',
                    'PortalUser',
                    'PortalAdmin',
                    'PortalLiteUser',
                ]) && (
                    <SidebarLink
                        customIcon={<BsExclamationTriangleFill className="nav-icon img-fluid inc-width" />}
                        label="Warranty Alerts"
                        url="/warranty-alerts"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    />
                )}

                {this.accountStore.isInRoles(['MriAdmin', 'MriUser', 'PortalUser', 'PortalAdmin']) && (
                    <SidebarLink
                        customIcon={<BsBuilding className="nav-icon img-fluid inc-width" />}
                        label="Accounts"
                        url="/accounts"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    />
                )}

                {this.accountStore.isInRoles(['MriAdmin', 'PortalAdmin']) && (
                    <SidebarLink
                        customIcon={<BsPeopleFill className="nav-icon img-fluid inc-width" />}
                        label="Users"
                        url="/users"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    >
                        {this.accountStore.isInRoles(['MriAdmin']) && (
                            <SidebarLink
                                label="All"
                                url={`/users/${UserRoleViews.All}`}
                                onClick={this.props.closeSidebar}
                                match={this.props.match}
                                location={this.props.location}
                                history={this.props.history}
                                staticContext={this.props.staticContext}
                                sidebarCollapsed={this.props.sidebarCollapsed}
                                child
                            />
                        )}

                        <SidebarLink
                            label="Portal"
                            url={`/users/${UserRoleViews.Portal}`}
                            onClick={this.props.closeSidebar}
                            match={this.props.match}
                            location={this.props.location}
                            history={this.props.history}
                            staticContext={this.props.staticContext}
                            sidebarCollapsed={this.props.sidebarCollapsed}
                            child
                        />

                        {this.accountStore.isInRoles(['MriAdmin']) && (
                            <SidebarLink
                                label="Production"
                                url={`/users/${UserRoleViews.Production}`}
                                onClick={this.props.closeSidebar}
                                match={this.props.match}
                                location={this.props.location}
                                history={this.props.history}
                                staticContext={this.props.staticContext}
                                sidebarCollapsed={this.props.sidebarCollapsed}
                                child
                            />
                        )}

                        {this.accountStore.isInRoles(['MriAdmin']) && (
                            <SidebarLink
                                label="Admins"
                                url={`/users/${UserRoleViews.Admin}`}
                                onClick={this.props.closeSidebar}
                                match={this.props.match}
                                location={this.props.location}
                                history={this.props.history}
                                staticContext={this.props.staticContext}
                                sidebarCollapsed={this.props.sidebarCollapsed}
                                child
                            />
                        )}

                        <SidebarLink
                            label="Invites"
                            url={`/invites`}
                            onClick={this.props.closeSidebar}
                            match={this.props.match}
                            location={this.props.location}
                            history={this.props.history}
                            staticContext={this.props.staticContext}
                            sidebarCollapsed={this.props.sidebarCollapsed}
                            child
                        />
                    </SidebarLink>
                )}

                {this.accountStore.isInRoles([
                    'MriAdmin',
                    'MriUser',
                    'PortalUser',
                    'PortalAdmin',
                    'PortalLiteUser',
                ]) && (
                    <SidebarLink
                        customIcon={<BsQuestionCircle className="nav-icon img-fluid inc-width" />}
                        label="Support"
                        url="/support"
                        disableLink
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    >
                        {this.accountStore.isInRoles(['MriAdmin', 'MriUser', 'PortalUser', 'PortalAdmin']) && (
                            <>
                                <SidebarLink
                                    label="Tickets"
                                    url="/support/tickets"
                                    onClick={this.props.closeSidebar}
                                    match={this.props.match}
                                    location={this.props.location}
                                    history={this.props.history}
                                    staticContext={this.props.staticContext}
                                    sidebarCollapsed={this.props.sidebarCollapsed}
                                    child
                                />

                                <SidebarLink
                                    label="Documents"
                                    url="/support/documents"
                                    onClick={this.props.closeSidebar}
                                    match={this.props.match}
                                    location={this.props.location}
                                    history={this.props.history}
                                    staticContext={this.props.staticContext}
                                    sidebarCollapsed={this.props.sidebarCollapsed}
                                    //linkNotificationCount={!this.accountStore.user!.acceptedDocumentsTAndC ? 1 : undefined}
                                    child
                                />

                                <SidebarLink
                                    label="Help Center"
                                    externalLink
                                    url="https://support.mri-inc.net/"
                                    onClick={this.props.closeSidebar}
                                    match={this.props.match}
                                    location={this.props.location}
                                    history={this.props.history}
                                    staticContext={this.props.staticContext}
                                    sidebarCollapsed={this.props.sidebarCollapsed}
                                    child
                                />
                            </>
                        )}

                        {this.accountStore.isInRoles([
                            'MriAdmin',
                            'MriUser',
                            'PortalUser',
                            'PortalAdmin',
                            'PortalLiteUser',
                        ]) && (
                            <SidebarLink
                                label="Contact Us"
                                url="/support/contact"
                                onClick={this.props.closeSidebar}
                                match={this.props.match}
                                location={this.props.location}
                                history={this.props.history}
                                staticContext={this.props.staticContext}
                                sidebarCollapsed={this.props.sidebarCollapsed}
                                child
                            />
                        )}
                    </SidebarLink>
                )}

                {this.accountStore.isInRoles(['MriAdmin', 'MriUser', 'PortalUser', 'PortalAdmin']) && (
                    <SidebarLink
                        customIcon={<img className="nav-icon img-fluid inc-width2" src={analyticsIcon} />}
                        label="Business Intelligence"
                        url="/bi"
                        onClick={this.props.closeSidebar}
                        match={this.props.match}
                        location={this.props.location}
                        history={this.props.history}
                        staticContext={this.props.staticContext}
                        sidebarCollapsed={this.props.sidebarCollapsed}
                    >
                        <SidebarLink
                            label="Dashboards"
                            url="/bi/dashboards"
                            onClick={this.props.closeSidebar}
                            match={this.props.match}
                            location={this.props.location}
                            history={this.props.history}
                            staticContext={this.props.staticContext}
                            sidebarCollapsed={this.props.sidebarCollapsed}
                            child={true}
                        />

                        <SidebarLink
                            label="Reports"
                            url="/bi/reports"
                            onClick={this.props.closeSidebar}
                            match={this.props.match}
                            location={this.props.location}
                            history={this.props.history}
                            staticContext={this.props.staticContext}
                            sidebarCollapsed={this.props.sidebarCollapsed}
                            child={true}
                        />
                    </SidebarLink>
                )}
            </ul>
        );
    }

    @computed
    get renderFooter() {
        return (
            <div className="sidebar-polygon">
                <img className="collapsed-polygon" src={mountainIcon} />
            </div>
        );
    }

    @computed
    get sidebarTitle() {
        return (
            <LinkContainer to="/" className="w-100 text-center">
                <a className="">
                    {(!this.props.sidebarCollapsed || this.appStore.isMedium) && (
                        <img className="img-fluid p-6" src={boldvuIcon} />
                    )}

                    {this.props.sidebarCollapsed && !this.appStore.isMedium && (
                        <img className="img-fluid" src={collapsedIcon} />
                    )}
                </a>
            </LinkContainer>
        );
    }

    @computed
    get collapsedClass() {
        if (this.props.sidebarCollapsed && !this.appStore.isMobile) {
            return ' collapsed-sidebar';
        }
        return '';
    }

    @inject
    private accountStore!: AccountStore;
    @inject
    private appStore!: AppStore;
}
